import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useBackHandler } from '@/lib/backButton';
import { cn } from '@/lib/utils';

export type SheetAction = {
  label: string;
  icon: string;
  /** Удалить, отклонить — красным. Подтверждение решает вызывающий (`useConfirm`): лист только выбирает действие. */
  danger?: boolean;
  onSelect: () => void;
};

/**
 * Лист действий «⋯» сотрудника на телефоне (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §3, правило 4): выезжает снизу,
 * строки по 52 px со значком и подписью, опасные — красным, внизу «Отмена». Значки действий в строке списка
 * (карандаш, корзина, стрелки) на 390 px не помещаются рядом с названием — они уходят сюда.
 *
 * Закрывается касанием вне листа, «Отменой», Esc и кнопкой «Назад» Android (`useBackHandler`, как у окна).
 */
export function ActionSheet({
  open,
  onClose,
  title,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  actions: SheetAction[];
}) {
  const { t } = useTranslation();
  const handleBack = useCallback(() => {
    onClose();
    return true;
  }, [onClose]);
  useBackHandler(handleBack, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    // `margin: 0` стилем — лист рисуется внутри `space-y-*`, тот дал бы слою `margin-top` (как у `Modal`, 13.10.2026).
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-end" style={{ margin: 0 }} onClick={onClose}>
      <div className="absolute inset-0 bg-inverse-surface/40" aria-hidden />
      <div
        data-action-sheet
        onClick={(e) => e.stopPropagation()}
        className="relative w-full rounded-t-[26px] bg-surface-container-lowest px-3 pb-[calc(0.75rem+var(--sa-bottom))] pt-2"
      >
        {title && <p className="px-3 pb-1 pt-2 text-[12px] text-on-surface-variant">{title}</p>}
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            onClick={() => {
              onClose();
              a.onSelect();
            }}
            className={cn(
              'flex h-[52px] w-full items-center gap-3 rounded-2xl px-3 text-left text-[15px] font-semibold',
              a.danger ? 'text-error' : 'text-on-surface',
            )}
          >
            <span className="material-symbols-outlined text-[20px]">{a.icon}</span>
            {a.label}
          </button>
        ))}
        <button
          type="button"
          onClick={onClose}
          className="mt-1 flex h-[52px] w-full items-center justify-center rounded-2xl bg-surface-container-low text-[15px] font-semibold text-on-surface"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
